import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { mapList } from '../common/rest.operators';

import {
    ExtGetTranslationsRequest,
    I18nIdMap,
    NgxTranslationModelTransformer,
    Translation,
    TranslationModelSetTransformer,
} from './i18n.model';

@Injectable()
export class I18nLoaderService extends TranslateLoader {
    events: Subject<any>; // TODO revise
    pending: boolean;
    private readonly _map: I18nIdMap;
    private _textIds: number[];
    private _cachedTextIds: { [lang: string]: number[] };
    private _translations: any;
    disabled: boolean; // TODO revise. see app.init

    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        super();
        this.events = new Subject();
        this._map = {};
        this._textIds = [];
        this._cachedTextIds = {};
        this._translations = {};
        this.disabled = false;
    }

    getTranslation(lang: string): Observable<any> {
        if (this.disabled) {
            return of<any>([]);
        }

        this._cachedTextIds[lang] = this._cachedTextIds[lang] || [];
        this._translations[lang] = this._translations[lang] || [];

        // eslint-disable-next-line @typescript-eslint/prefer-includes
        const requested = this._textIds.filter(
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            (value) => this._cachedTextIds[lang].indexOf(value) === -1,
        );
        if (requested.length === 0) {
            return of<any>(this._translations[lang]);
        }

        const body: ExtGetTranslationsRequest = {
            translationIDs: requested.join(','),
            languageID: Number.parseInt(lang),
        };

        this.pending = true;
        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.TRANSLATION}/translation/GetTranslationsWithoutAuth`,
                body,
            )
            .pipe(
                mapList(new TranslationModelSetTransformer()),
                tap((model: Translation[]) => {
                    this._cachedTextIds[lang] =
                        this._cachedTextIds[lang].concat(requested);
                    return model;
                }),
                map((model: Translation[]) => {
                    const translations = new NgxTranslationModelTransformer(
                        this._map,
                    ).transform(model);
                    Object.assign(this._translations[lang], translations);
                    return this._translations[lang];
                }),
                catchError((error) => {
                    console.error(error);
                    return of<any>(this._translations[lang]);
                }),
                finalize(() => {
                    this.pending = false;
                    this.events.next({ type: 'load' });
                }),
            );
    }

    registerTranslationIdMap(idMap: I18nIdMap): void {
        Object.assign(this._map, idMap);
        const textIds = Object.values(idMap)
            .map((e) => e?.textId)
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            .filter(
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                (value) => value != null && this._textIds.indexOf(value) === -1,
            );
        this._textIds = this._textIds.concat(textIds);
        if (textIds.length !== 0) {
            this.events.next({ type: 'map', map: idMap, textIds });
        }
    }
}
